import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as UploadIcon } from "assets/images/icons/icn_btn_export_pressed.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import CaretRightFilled from "@ant-design/icons/CaretRightFilled";
import WarningFilled from "@ant-design/icons/WarningFilled";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { useTheme } from "@mui/material/styles";
import zIndex from '@mui/material/styles/zIndex';
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";

// Component
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { MenuSelect } from "components/Select/menuSelect";
import GlobalAlert from "components/Notification";

import LoadingElement from "./LoadingElement";
import MacAddressTableRow from "./TableRow";
import UploadView from "./UploadView";
import AdoptView from "./AdoptView";

// Const
import { defaultMacAddressItem, defaultMacAddressObj } from "./util/const";

// Util
import Form from "./util/form";
import { getAPIKeyAndParams } from "./util/func";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const MainView = ({
  auth,
  viewStateEnum,
  getViewTitle,
  viewState,
  changeViewState,
  onCloseModal,
  setShowPreConfirm,
}) => {
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();
  const form = Form.init(theme);

  // Status
  const viewRef = useRef(null);
  const adoptViewRef = useRef(null);
  const [uuidList, setUuidList] = useState({
    "sites": uuidv4(),
    "preRegister": uuidv4(),
    "preConfirm": uuidv4(),
  });
  const [sites, setSites] = useState([]);
  const [sitesPinningOptions, setSitesPinningOptions] = useState([
    { label: "Inventory", value: "Inventory" },
  ]);
  const [readyToRenderTable, setReadyToRenderTable] = useState(false);
  const [formValue, setFormValue] = useState(null);
  const [registerMacAddressItems, setRegisterMacAddressItems] = useState([]);
  const [adoptFinish, setAdoptFinish] = useState(false);
  const [forTestOnly, setForTestOnly] = useState(1); // Test 4 times re-fetch
  const [preConfirmData, setPreConfirmData] = useState(null);
  const [isPreConfirmLoading, setIsPreConfirmLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ open: false, severity: "error", message: "" });

  // Method
  const fetchSites = () => {
    const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'sites');
    sendRequest(
      apiKey,
      uuidList.sites,
      placeholder,
      null,
    );
  }

  const init = () => {
    setFormValue(cloneDeep(defaultMacAddressObj));
    // Defer table rendering slightly to let modal render
    setTimeout(() => setReadyToRenderTable(true), 1000);
  }

  const clickUploadButton = () => {
    changeViewState(viewStateEnum.UPLOAD);
  }

  const changeMacAddressItemValue = (index, key, value) => {
    const newFormValue = cloneDeep(formValue);
    newFormValue.macAddresses[index][key] = value;
    newFormValue.macAddresses[index].macAddressDirty = true;
    setFormValue(newFormValue);
  }

  const changeSnInformationItemValue = (index, key, value) => {
    const newFormValue = cloneDeep(formValue);
    newFormValue.macAddresses[index][key] = value;
    setFormValue(newFormValue);
  }

  const handlePasteMacAddress = (event, index) => {
    const clipboardText = (event.clipboardData || window.clipboardData).getData('text');
    if (!clipboardText || index === undefined) return;

    event.preventDefault();

    // Split clipboard text by common delimiters and clean each part to extract valid MAC addresses
    const rawParts = clipboardText.split(/[\s,;]+/);
    const macList = [];

    // Process each part to extract valid MAC addresses
    for (const part of rawParts) {
      const hex = part.replace(/[^0-9a-fA-F]/g, '');
      if (hex.length === 12) {
        macList.push(hex.match(/.{1,2}/g).join('-').toUpperCase());
      }
    }

    // If no valid MAC addresses found, do nothing
    if (macList.length === 0) return;

    // If valid MAC addresses found, update the form value
    setFormValue((prev) => {
      const newFormValue = cloneDeep(prev);
      if (macList.length === 1) {
        newFormValue.macAddresses[index] = { ...defaultMacAddressItem, macAddress: macList[0], macAddressDirty: true };
      } else {
        newFormValue.macAddresses[index] = { ...defaultMacAddressItem, macAddress: macList[0], macAddressDirty: true };
        for (let i = 1; i < macList.length; i++) {
          newFormValue.macAddresses.splice(index + i, 0, { ...defaultMacAddressItem, macAddress: macList[i], macAddressDirty: true });
        }
      }
      return newFormValue;
    });
  };


  const onDeleteMacAddress = (index) => {
    const newFormValue = cloneDeep(formValue);
    newFormValue.macAddresses.splice(index, 1);
    setFormValue(newFormValue);
  }

  const clickAddButton = () => {
    const newFormValue = cloneDeep(formValue);
    newFormValue.macAddresses.push(cloneDeep(defaultMacAddressItem));
    setFormValue(newFormValue);
  }

  const checkFormValid = () => {
    // Check MAC Address, SN information, and site selection
    if (auth.scopeLevel === 'org' && !formValue.siteId) return false;

    for (let macItem of formValue.macAddresses) {
      const macRegex = form.validateMacAddress(macItem.macAddress);
      if (!macRegex || !macItem.snInformation?.trim()) return false;
    }

    return true;
  }

  const sendAdoptRequest = () => {
    // Send API request
    const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'preRegister');

    // Change macAddresses format from "AA-BB-CC-DD-EE-FF" to "AA:BB:CC:DD:EE:FF"
    const params = {
      siteId: formValue.siteId === 'Inventory' ? null : formValue.siteId,
      devices: formValue.macAddresses.map((item) => ({
        macAddress: item.macAddress.replace(/-/g, ":"),
        sn: item.snInformation,
      })),
    };

    if (auth.scopeLevel === 'site') {
      params.siteId = placeholder.siteId;
    }

    sendRequest(
      apiKey,
      uuidList.preRegister,
      placeholder,
      params,
    );

    // Save to registerMacAddressItems
    const newRegisterMacAddressItems = formValue.macAddresses.map((item) => {
      return {
        macAddress: item.macAddress,
        snInformation: item.snInformation,
        isAp: false,
        isSwitch: false,
        modelName: null,
        result: null,
      };
    });
    setRegisterMacAddressItems(newRegisterMacAddressItems);
    setAdoptFinish(false);

    // Change to Adopt View
    changeViewState(viewStateEnum.ADOPT);
  }

  const sendApplyRequest = () => {
    const siteId = auth.scopeLevel === 'site'
      ? auth.sites?.id
      : formValue.siteId === 'Inventory' ? null : formValue.siteId;
    // Inventory has no site subscription to confirm.
    if (!siteId) {
      sendAdoptRequest();
      return;
    }

    const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'preConfirm');
    setPreConfirmData(null);
    setShowPreConfirm(false);
    setAlertInfo((current) => ({ ...current, open: false }));
    setIsPreConfirmLoading(true);
    const requestId = uuidv4();
    setUuidList((current) => ({ ...current, preConfirm: requestId }));
    sendRequest(apiKey, requestId, placeholder, {
      siteId,
      devices: formValue.macAddresses.map((item) => ({
        macAddress: item.macAddress.replace(/-/g, ":"),
        sn: item.snInformation,
      })),
    });
  }

  const sendTestApplyRequest = () => {
    const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'preRegister');
    const params = {
      siteId: formValue.siteId === 'Inventory' ? null : formValue.siteId,
      devices: formValue.macAddresses.map((item) => ({
        macAddress: item.macAddress,
        sn: item.snInformation,
      })),
    };

    if (auth.scopeLevel === 'site') {
      params.siteId = placeholder.siteId;
    }

    sendRequest(
      apiKey,
      uuidList.preRegister,
      placeholder,
      params,
    );
  }

  function HandleUploadView() {
    return {
      closeView: () => {
        changeViewState(viewStateEnum.MAIN);
      },
      updateMacAddressItemsList: (replaceFlag, newMacAddressItems) => {
        const firstMac = newMacAddressItems[0]?.macAddress || "";

        if (firstMac.includes(":")) {
          // Convert all MAC addresses to use "-" as separator
          newMacAddressItems.forEach((item, index) => {
            let formattedMac = item.macAddress.toUpperCase().replace(/[:]/g, "-");
            newMacAddressItems[index].macAddress = formattedMac;
          });
        }
        if (!firstMac.includes(":") && !firstMac.includes("-")) {
          // Convert all MAC addresses to use "-" as separator
          newMacAddressItems.forEach((item, index) => {
            let formattedMac = item.macAddress.toUpperCase();
            if (formattedMac.length === 12) {
              formattedMac = formattedMac.match(/.{1,2}/g).join("-");
            }
            newMacAddressItems[index].macAddress = formattedMac;
          });
        }

        setFormValue((prev) => {
          // Remove empty MAC address entries from previous list
          const filteredPrevMacAddresses = prev.macAddresses.filter(item => item.macAddress && item.macAddress.trim() !== "");
          prev.macAddresses = filteredPrevMacAddresses;

          const finalMacAddressItems = [...prev.macAddresses];
          if (replaceFlag) {
            // Combine existing and new MAC addresses, if MAC address exists, overwrite description
            newMacAddressItems.forEach((newItem) => {
              const existingIndex = finalMacAddressItems.findIndex(item => item.macAddress === newItem.macAddress);
              if (existingIndex !== -1) {
                // Overwrite existing entry
                finalMacAddressItems[existingIndex].snInformation = newItem.snInformation;
              } else {
                // Add new entry
                finalMacAddressItems.push(newItem);
              }
            });
          } else {
            // Merging
            finalMacAddressItems.push(...newMacAddressItems);
          }

          return {
            ...prev,
            macAddresses: finalMacAddressItems,
          };
        });
      }
    }
  }

  function HandleAdoptView() {
    return {
      closeView: () => {
        onCloseModal();
      },
      exportCsv: () => {
        // Export CSV logic
        // MAC Address,SN Information,Model Name, Result
        // AA:BB:CC:DD:EE:FF,12345678901234,DAP-3060,Success
        let csvContent = "MAC Address,SN Information,Model Name,Result\n";
        registerMacAddressItems.forEach((item) => {
          const resultText = item.result === "success" ? "Success" : item.result === "failure" ? "Failure" : item.result === "error" ? "Error" : "";
          csvContent += `${item.macAddress},${item.snInformation},${item.modelName || ""},${resultText}\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "pre_registered_devices.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  // Side effect
  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    const sitesResponse = getData(uuidList.sites);
    if (sitesResponse) {
      if (sitesResponse.hasOwnProperty('code')) {
        // Error handling can be added here
      } else {
        const updatedSites = sitesResponse.data.sites.map((site) => ({
          label: site.siteName,
          value: site.siteId,
        }));

        setSites(updatedSites);
      }

      init();
    }

    const preRegisterResponse = getData(uuidList.preRegister);
    if (!adoptFinish && preRegisterResponse) {
      const preRegisterError = preRegisterResponse.error;
      const preRegisterErrorCode = preRegisterError?.code ?? preRegisterResponse.code;
      if (preRegisterError || (preRegisterErrorCode !== undefined && Number(preRegisterErrorCode) !== 0)) {
        setRegisterMacAddressItems((items) => items.map((item) => ({
          ...item,
          result: "error",
        })));
        setAdoptFinish(true);
      } else {
        let data = [];

        // Environment to control
        // 1. For mockup: simulate partial processing by slicing data
        // 2. For real API: use all data returned
        if (checkMockup === 'true') {
          const newForTestOnly = forTestOnly + 1;

          if (newForTestOnly < 5) {
            setTimeout(() => {
              sendTestApplyRequest();
            }, Math.floor(Math.random() * 30000) + 1000); // Random time 1 to 30 seconds
          }
          setForTestOnly(newForTestOnly);

          let startIndex = 0;
          let endIndex = 3;

          if (forTestOnly === 2) {
            startIndex = 3;
            endIndex = 6;
          }

          if (forTestOnly === 3) {
            startIndex = 6;
            endIndex = 8;
          }

          data = preRegisterResponse.data.slice(startIndex, endIndex);

          if (forTestOnly === 4) {
            data = [];
          }
        } else {
          data = preRegisterResponse.data;
        }

        // When no data returned(last batch), mark remaining items as failure
        if (data.length === 0) {
          const updatedRegisterMacAddressItems = registerMacAddressItems.map((item) => {
            if (!item.result) {
              return {
                ...item,
                result: "failure",
              };
            }
            return item;
          });

          setRegisterMacAddressItems(updatedRegisterMacAddressItems);
          setAdoptFinish(true);
          return;
        }

        // Update registerMacAddressItems with modelName and result
        const updatedRegisterMacAddressItems = registerMacAddressItems.map((item) => {
          const matchedDevice = data.find(device => device.macAddress === item.macAddress.replace(/-/g, ":"));

          if (!matchedDevice) {
            return item;
          }

          // const prefix = matchedDevice.modelName.split('-')[0];
          // const isAp = /D?P/.test(prefix);
          // const isSwitch = /D?X?S/.test(prefix);

          return {
            ...item,
            // isAp,
            // isSwitch,
            modelName: matchedDevice.modelName,
            result: matchedDevice.code === 0 ? "success" : "failure",
          };
        });

        setRegisterMacAddressItems(updatedRegisterMacAddressItems);
      }
    }

    const preConfirmResponse = getData(uuidList.preConfirm);
    if (preConfirmResponse && isPreConfirmLoading) {
      setIsPreConfirmLoading(false);
      const preConfirmError = preConfirmResponse.error;
      const preConfirmErrorCode = preConfirmError?.code ?? preConfirmResponse.code;
      if (preConfirmError || (preConfirmErrorCode !== undefined && Number(preConfirmErrorCode) !== 0)) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: preConfirmError?.message || preConfirmResponse.message || preConfirmResponse.msg || "Unable to confirm service changes.",
        });
      } else {
        const resultData = preConfirmResponse.data;
        const items = (Array.isArray(resultData) ? resultData : resultData ? [resultData] : []);
        if (!items.length) {
          setAlertInfo({ open: true, severity: "error", message: "The pre-confirm response did not include site information." });
        } else if (items.some((item) => Array.isArray(item.service) && item.service.length > 0)) {
          setPreConfirmData(items);
          setShowPreConfirm(true);
        } else {
          sendAdoptRequest();
        }
      }
    }
  }, [dataMap, registerMacAddressItems, adoptFinish, isPreConfirmLoading, changeViewState]);

  useEffect(() => {
    if (registerMacAddressItems.length === 0) return;

    // Timeout to mark remaining unprocessed items as failure
    let timer = null;
    let seconds = 60 * 1000 * 5; // Default 5 minutes

    timer = setTimeout(() => {
      const newRegisterMacAddressItems = registerMacAddressItems.map((item) => {
        if (!item.result) {
          return {
            ...item,
            result: "failure",
          };
        }
        return item;
      });
      setRegisterMacAddressItems(newRegisterMacAddressItems);
      setAdoptFinish(true);
    }, seconds);

    return () => clearTimeout(timer);
  }, [registerMacAddressItems]);

  if (!readyToRenderTable) {
    return <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          {getViewTitle(viewStateEnum.MAIN)}
        </Typography>
      </Box>
      <LoadingElement />
    </>
  }

  // Move Adopt view up to cover the other views
  let viewHeight = viewRef && viewRef.current ? viewRef.current.offsetHeight : 0;
  let marginTop = 0;
  if (adoptViewRef && adoptViewRef.current) {
    viewHeight = adoptViewRef.current.offsetHeight;
    marginTop = -viewHeight / 2;
  }

  const closePreConfirm = () => {
    setPreConfirmData(null);
    setShowPreConfirm(false);
  };

  return (
    <>
      {preConfirmData && (
        <>
          <Box sx={{ position: "relative" }}>
            <Stack spacing={2.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: "#172664", fontSize: "18px", fontWeight: "bold" }}>
                  Confirm Service Change
                </Typography>
                <IconButton onClick={onCloseModal} sx={{ p: 0.5 }} aria-label="Close">
                  <CloseOutlined style={{ fontSize: "18px", color: "rgba(0,0,0,0.45)" }} />
                </IconButton>
              </Stack>
              <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
                <Stack spacing={2}>
                  {preConfirmData.map((item, index) => {
                    const serviceNames = { healthAI: "Health AI Insight", nmsPro: "NMS Pro", unityPro: "Nuclias Unity Portal" };
                    const serviceText = (item.service || []).map((service) => serviceNames[service] || service).join(", ") || "Subscribed Services";
                    const actionText = item.action === "add" ? "Added Device" : item.action === "remove" ? "Remove Device" : item.action;
                    const dailyRate = Number(item.dailyRate || 0);
                    const totalCredits = dailyRate * Number(item.remainingDays || 0);
                    return (
                      <Stack key={`${item.siteId || item.siteName || "site"}-${index}`} spacing={2} sx={{ mb: index < preConfirmData.length - 1 ? 3 : 0 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CaretRightFilled style={{ fontSize: "14px", color: "#172664" }} />
                            <Typography sx={{ color: "rgba(0,0,0,0.85)", fontSize: "15px", fontWeight: "bold" }}>{item.siteName || "Site"}</Typography>
                          </Stack>
                          <Typography sx={{ color: "#172664", fontSize: "16px", fontWeight: "bold", mb: 0.5 }}>Service: {serviceText}</Typography>
                          <Divider sx={{ mb: 1.5 }} />
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1}><Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>Action:</Typography><Typography variant="body2" sx={{ color: "#172664", fontWeight: "bold" }}>{actionText}</Typography></Stack>
                            <Stack direction="row" spacing={1}><Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>New Devices:</Typography><Typography variant="body2" sx={{ color: "#008514", fontWeight: "bold" }}>{item.deviceCount} (Total: {item.deviceTotalCount})</Typography></Stack>
                            <Stack direction="row" spacing={1}><Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>Rate:</Typography><Typography variant="body2" sx={{ color: "#373737", fontWeight: "bold" }}>{item.rate || ""}</Typography></Stack>
                          </Stack>
                        </Stack>
                        <Box sx={{ p: 2, border: "1px solid rgba(0,0,0,0.15)", borderRadius: "12px" }}>
                          <Typography sx={{ color: "#172664", fontSize: "14px", fontWeight: "bold", mb: 2 }}>This Month’s Prorated Credit Deduction</Typography>
                          <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="textSecondary">Daily Rate</Typography><Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}><Box component="span" sx={{ color: "#008514" }}>{dailyRate.toLocaleString()}</Box> Credits / day</Typography></Stack>
                            <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="textSecondary">Remaining Days</Typography><Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}><Box component="span" sx={{ color: "#008514" }}>{item.remainingDays}</Box> Days</Typography></Stack>
                            <Stack direction="row" justifyContent="space-between"><Typography variant="body2" color="textSecondary">Total</Typography><Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}><Box component="span" sx={{ color: "#008514" }}>{totalCredits.toLocaleString()}</Box> Credits</Typography></Stack>
                          </Stack>
                        </Box>
                      </Stack>
                    );
                  })}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <WarningFilled style={{ color: "#D1003B", fontSize: "18px" }} />
                    <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.85)", fontWeight: "bold" }}>
                      {preConfirmData.some((item) => item.sufficientCredits === false) ? "Insufficient credits. Please contact your Organization Super Admin." : "Changes take effect immediately upon confirmation."}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 1 }}>
                <OutlineButton onClick={closePreConfirm} width="120px" height="36px" sx={{ borderRadius: "20px" }}>Cancel</OutlineButton>
                <SolidButton onClick={() => { closePreConfirm(); sendAdoptRequest(); }} disabled={preConfirmData.some((item) => item.sufficientCredits === false)} width="120px" height="36px" sx={{ borderRadius: "20px", bgcolor: "#172664" }}>Confirm</SolidButton>
              </Stack>
            </Stack>
          </Box>
        </>
      )}
      {!preConfirmData && <Box
        ref={viewRef}
        style={{
          position: "relative",
          padding: '0',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              paddingLeft: "20px",
            }}
          >
            {getViewTitle(viewStateEnum.MAIN)}
          </Typography>
          <Button
            onClick={onCloseModal}
            sx={{
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </Box>}

      <Box sx={{ display: preConfirmData ? "none" : "block" }}>
        {
          viewState !== viewStateEnum.ADOPT && (
            <>
              {/* Body */}
              <Box sx={{ pl: 1, pr: 2 }}>
                {/* Upload Button */}
                <Box sx={form.getRowStyle()}>
                  <Box sx={form.getLabelStyle()}>Add MAC Address</Box>
                  <Box display="flex" gap="10px" justifyContent={"flex-end"} sx={{ flexGrow: 1 }}>
                    <OutlineRectangleButton
                      startIcon={<UploadIcon />}
                      height="32px"
                      onClick={clickUploadButton}
                    >
                      Upload
                    </OutlineRectangleButton>
                  </Box>
                </Box>

                {/* Table */}
                <Box sx={{ ...form.getTableStyle() }}>
                  <TableContainer
                    component={Paper}
                    sx={{
                      "&.MuiPaper-root": {
                        boxShadow: "none",
                        border: 'none',
                      },
                    }}
                    style={{ height: '300px', overflowY: 'auto' }}
                  >
                    <Table
                      sx={{
                        borderCollapse: "collapse",
                        "& td, & th": {
                          border: "none",
                        },
                        "& tbody tr:nth-of-type(odd)": {
                          backgroundColor: theme.palette.primary.white,
                        },
                        "& tbody tr:nth-of-type(even)": {
                          backgroundColor: 'rgb(246, 246, 246)',
                        },
                      }}
                    >
                      <TableHead
                        sx={{
                          borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
                        }}
                      >
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>MAC Address</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>SN Information</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          formValue.macAddresses.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                sx={{
                                  textAlign: "center",
                                  padding: "20px",
                                }}
                              >
                                No Entry in the table
                              </TableCell>
                            </TableRow>
                          ) : (
                            <>
                              {
                                formValue.macAddresses.map((mac, index) => (
                                  <>
                                    <MacAddressTableRow
                                      key={index}
                                      form={form}
                                      originalMacAddresses={formValue.macAddresses}
                                      mac={mac}
                                      index={index}
                                      onMacAddressChange={changeMacAddressItemValue}
                                      onSnInformationChange={changeSnInformationItemValue}
                                      onDeleteMacAddress={onDeleteMacAddress}
                                      onPasteMacAddress={handlePasteMacAddress}
                                    />
                                  </>
                                ))
                              }
                            </>
                          )
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Add Button */}
                <Box sx={{ ...form.getTableStyle() }} style={{ justifyContent: 'flex-end' }}>
                  <OutlineRectangleButton
                    startIcon={<AddIcon />}
                    height="32px"
                    onClick={clickAddButton}
                  >
                    Add
                  </OutlineRectangleButton>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Add to Site */}
                {
                  auth.scopeLevel !== 'site' && (
                    <>
                      <Box
                        sx={{ ...form.getTableStyle() }}
                        style={{
                          justifyContent: 'flex-start',
                          alignItems: 'center'
                        }}
                      >
                        <Box sx={form.getLabelStyle()}>Add to Site</Box>
                        <MenuSelect
                          value={formValue?.siteId ?? ""}
                          options={sites}
                          pinningOptions={sitesPinningOptions}
                          placeholder="Select Site"
                          width={200}
                          onChange={(opt) => {
                            const newFormValue = cloneDeep(formValue);
                            newFormValue.siteId = opt ? opt.value : "";
                            setFormValue(newFormValue);
                          }}
                        />
                      </Box>
                    </>
                  )
                }

                {/* Next Button */}
                <Box
                  sx={{
                    margin: "24px 0 16px auto",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <SolidButton
                    onClick={sendApplyRequest}
                    width="100px"
                    height="32px"
                    disabled={!checkFormValid() || isPreConfirmLoading}
                  >
                    Next
                  </SolidButton>
                </Box>
              </Box>
            </>
          )
        }

        {
          viewState === viewStateEnum.UPLOAD && (
            <>
              <Box style={{
                ...form.getInnerViewStyle(zIndex),
                // width: viewWidth + 40 + 'px',
                // transform: `translateY(${marginTop}px)`,
              }} >
                <Box style={form.getInnerContainerStyle()}>
                  <UploadView
                    viewStateEnum={viewStateEnum}
                    getViewTitle={getViewTitle}
                    form={form}
                    handleUploadView={HandleUploadView()}
                  />
                </Box>
              </Box>
            </>
          )
        }

        {
          viewState === viewStateEnum.ADOPT && (
            <>
              <Box style={{
                ...form.getInnerViewStyle(zIndex),
                // width: viewWidth + 40 + 'px',
                transform: `translateY(${marginTop}px)`,
              }} >
                <Box style={form.getInnerContainerStyle()} ref={adoptViewRef}
                >
                  <AdoptView
                    viewStateEnum={viewStateEnum}
                    getViewTitle={getViewTitle}
                    form={form}
                    registerMacAddressItems={registerMacAddressItems}
                    handleAdoptView={HandleAdoptView()}
                  />
                </Box>
              </Box>
            </>
          )
        }
      </Box>
      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((current) => ({ ...current, open: false }))}
      />
    </>
  );
}

export default MainView;
